import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Pencil, Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import SelectWithAdd from "@/components/SelectWithAdd";
import AddNewDialog from "@/components/AddNewDialog";
import BulkUploadButton from "@/components/BulkUploadButton";
import StatusBadge from "@/components/StatusBadge";
import DatePickerField from "@/components/DatePickerField";
import { useAuth } from "@/contexts/AuthContext";
import { Th, Td } from "@/pages/CustomersPage";

const EMPTY = {
  sim_service_provider_id: "",
  mobile_number: "",
  sim_no: "",
  status: "Active",
  sim_activation_date: "",
  sim_next_renewal_date: "",
  sim_allotted_user_id: "",
  sim_allotted_user_name: "",
};

export default function SIMsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [addProviderOpen, setAddProviderOpen] = useState(false);

  const load = () => api.get("/sims").then((r) => setItems(r.data));
  const loadProviders = () => api.get("/providers").then((r) => setProviders(r.data));
  const loadEmployees = () => api.get("/employees").then((r) => setEmployees(r.data));

  // Initial data load — load/loadProviders/loadEmployees reference stable `api`
  // and setState dispatchers. Intentionally one-shot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    loadProviders();
    loadEmployees();
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      sim_service_provider_id: row.sim_service_provider_id || "",
      mobile_number: row.mobile_number || "",
      sim_no: row.sim_no || "",
      status: row.status || "Active",
      sim_activation_date: row.sim_activation_date || "",
      sim_next_renewal_date: row.sim_next_renewal_date || "",
      sim_allotted_user_id: row.sim_allotted_user_id || "",
      sim_allotted_user_name: row.sim_allotted_user_name || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.sim_service_provider_id) {
      toast.error("SIM Service Provider is required");
      return;
    }
    if (!form.mobile_number.trim()) {
      toast.error("SIM Number is required");
      return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/sims/${editing.id}`, form);
      else await api.post("/sims", form);
      toast.success(editing ? "SIM record updated" : "SIM record created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete SIM record for ${row.mobile_number}?`)) return;
    try {
      await api.delete(`/sims/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const addProvider = async (vals) => {
    const { data } = await api.post("/providers", vals);
    await loadProviders();
    setForm((f) => ({ ...f, sim_service_provider_id: data.id }));
    toast.success("Provider added");
  };

  const filtered = useMemo(() => {
    const f = filter.toLowerCase();
    return items.filter((r) =>
      !f ||
      [r.mobile_number, r.sim_no, r.sim_service_provider_name].some((x) =>
        (x || "").toLowerCase().includes(f)
      )
    );
  }, [items, filter]);

  return (
    <div data-testid="sims-page">
      <PageHeader
        overline="Masters"
        title="SIMs Details"
        description="Catalogue of SIM cards held in stock — SIM number + SIM serial number, grouped by provider/account."
        actions={
          <div className="flex gap-2">
            <BulkUploadButton
              templateUrl="/sims/sample-template"
              uploadUrl="/sims/bulk-upload"
              templateName="sims_template.xlsx"
              label="Bulk Upload"
              testIdPrefix="sim-bulk"
              onDone={load}
            />
            <Button
              data-testid="sim-add-button"
              onClick={openNew}
              className="rounded-none vsm-primary-bg hover:opacity-90"
            >
              <Plus className="w-4 h-4 mr-2" /> Add SIM
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="relative max-w-md w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 vsm-muted" />
            <Input
              data-testid="sim-quick-search"
              className="rounded-none pl-9"
              placeholder="Quick search by SIM number, serial, provider…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="vsm-overline ml-auto">{filtered.length} record(s)</div>
        </div>

        <div className="border vsm-border vsm-surface overflow-x-auto vsm-table-scroll" data-testid="sims-table">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b vsm-border text-left">
                <Th>SIM Service Provider · Account</Th>
                <Th>SIM Number</Th>
                <Th>SIM Serial Number</Th>
                <Th>Allotted User</Th>
                <Th>Status</Th>
                <Th className="w-28 text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 vsm-muted text-center">
                    No SIM records yet.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b vsm-border hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                  <Td className="font-semibold">
                    {r.sim_service_provider_name || "—"}
                    {r.sim_service_provider_account_number && (
                      <span className="vsm-muted font-normal ml-1">· {r.sim_service_provider_account_number}</span>
                    )}
                  </Td>
                  <Td>{r.mobile_number}</Td>
                  <Td>{r.sim_no || "—"}</Td>
                  <Td>{r.sim_allotted_user_name || "—"}</Td>
                  <Td><StatusBadge status={r.status} /></Td>
                  <Td className="text-right">
                    <button
                      onClick={() => openEdit(r)}
                      data-testid={`sim-edit-${r.id}`}
                      className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => remove(r)}
                        data-testid={`sim-delete-${r.id}`}
                        className="p-1.5 hover:bg-red-100 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="rounded-none border vsm-border max-w-lg"
          data-testid="sim-dialog"
        >
          <DialogHeader>
            <div className="vsm-overline">{editing ? "Edit Record" : "New Record"}</div>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              {editing ? `Edit ${editing.mobile_number}` : "New SIM Record"}
            </DialogTitle>
            <DialogDescription className="vsm-muted text-sm">
              Provider and SIM number are required. SIM serial number and allotted user are optional.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label className="vsm-overline">
                SIM Service Provider · Account <span className="text-red-600">*</span>
              </Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId="sim-provider"
                  value={form.sim_service_provider_id}
                  onChange={(v) => setForm({ ...form, sim_service_provider_id: v })}
                  options={providers.map((p) => ({
                    id: p.id,
                    label: p.account_number ? `${p.name} · ${p.account_number}` : p.name,
                  }))}
                  onAddNew={() => setAddProviderOpen(true)}
                  placeholder="Select provider"
                />
              </div>
            </div>

            <div>
              <Label className="vsm-overline">
                SIM Number <span className="text-red-600">*</span>
              </Label>
              <Input
                data-testid="sim-mobile-number"
                className="rounded-none mt-2"
                value={form.mobile_number}
                onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
                placeholder="e.g. 9876543210"
              />
            </div>

            <div>
              <Label className="vsm-overline">SIM Serial Number</Label>
              <Input
                data-testid="sim-sim-no"
                className="rounded-none mt-2"
                value={form.sim_no}
                onChange={(e) => setForm({ ...form, sim_no: e.target.value })}
                placeholder="ICCID / Serial — optional"
              />
            </div>

            <div>
              <Label className="vsm-overline">SIM Allotted User</Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId="sim-allotted-user"
                  value={form.sim_allotted_user_id || "__none__"}
                  onChange={(v) => {
                    const emp = employees.find((e) => e.id === v);
                    setForm({
                      ...form,
                      sim_allotted_user_id: v === "__none__" ? "" : v,
                      sim_allotted_user_name: v === "__none__" ? "" : (emp?.name || ""),
                    });
                  }}
                  options={[
                    { id: "__none__", label: "— Unassigned —" },
                    ...employees.map((e) => ({ id: e.id, label: e.name })),
                  ]}
                  placeholder="Unassigned"
                />
              </div>
            </div>

            <div>
              <Label className="vsm-overline">Status</Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId="sim-status"
                  value={form.status || "Active"}
                  onChange={(v) => setForm({ ...form, status: v })}
                  options={[{ id: "Active", label: "Active" }, { id: "Inactive", label: "Inactive" }]}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="vsm-overline">SIM Activation Date</Label>
                <div className="mt-2">
                  <DatePickerField
                    testId="sim-activation-date"
                    value={form.sim_activation_date}
                    onChange={(v) => setForm({ ...form, sim_activation_date: v })}
                  />
                </div>
              </div>
              <div>
                <Label className="vsm-overline">SIM Next Renewal Date</Label>
                <div className="mt-2">
                  <DatePickerField
                    testId="sim-renewal-date"
                    value={form.sim_next_renewal_date}
                    onChange={(v) => setForm({ ...form, sim_next_renewal_date: v })}
                  />
                </div>
              </div>
            </div>

            {editing && (
              <div className="border vsm-border p-3 text-xs vsm-muted grid grid-cols-2 gap-2">
                <div>Added by: <span className="font-mono">{editing.added_by}</span></div>
                <div>Added: <span className="font-mono">{editing.added_date}</span></div>
                <div>Modified by: <span className="font-mono">{editing.modified_by}</span></div>
                <div>Modified: <span className="font-mono">{editing.modified_date}</span></div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-none"
                onClick={() => setOpen(false)}
                data-testid="sim-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-none vsm-primary-bg hover:opacity-90"
                data-testid="sim-save"
              >
                {saving ? "Saving…" : "Save Record"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AddNewDialog
        open={addProviderOpen}
        onOpenChange={setAddProviderOpen}
        title="Add Provider"
        fields={[
          { key: "name", label: "Provider Name", required: true },
          { key: "account_number", label: "Account Number", required: true },
        ]}
        onSubmit={addProvider}
        testIdPrefix="add-provider"
      />
    </div>
  );
}
